import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';

/**
 * Base wrapper for every ITGC check form.
 * Renders a bordered card with title, description, slotted children,
 * and a submit button.
 *
 * Props:
 *   title       - check name heading
 *   description - short explanation of what the check does
 *   onSubmit    - (e: FormEvent) => void — called on form submit
 *   isLoading   - disables the button and shows spinner while true
 *   children    - form field components
 */
export default function CheckFormWrapper({
  title,
  description,
  onSubmit,
  isLoading = false,
  children
}) {
  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      noValidate
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper'
      }}
    >
      <Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {description}
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {children}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            backgroundColor: 'black',
            '&:hover': { backgroundColor: '#333' },
            '&:disabled': { backgroundColor: '#aaa' }
          }}
        >
          {isLoading ? 'Processing...' : 'Run Check'}
        </Button>
      </Box>
    </Box>
  );
}
